import { lazy, Suspense, type ComponentProps, type ComponentType, type ReactNode } from 'react';

/**
 * Code-splitting that never makes the person wait twice (issue #208).
 *
 * `lazyComponent(() => import('./X'), 'X')` returns a component that renders
 * exactly like `X` — same props — but whose code is a separate chunk, fetched
 * the first time it renders or the first time someone calls `.preload()`.
 *
 * Three things it does that a bare `React.lazy` does not:
 *
 * 1. **Its own `<Suspense>`.** A call site swaps its import and nothing else.
 *    A bare lazy component with no boundary of its own suspends to the nearest
 *    one above it — for a dialog inside `MailView` that is the whole app — so
 *    a first press of `c` would blank the mail list while compose loads.
 * 2. **`.preload()`**, for intent (hover/focus of a trigger) and for the idle
 *    prefetch after first paint (`prefetch.ts`). It is the same promise the
 *    render uses, so a chunk is fetched once however it is asked for.
 * 3. **No fallback flash once loaded.** `React.lazy` suspends on first render
 *    even when the module arrived long ago (it only learns that from a
 *    `.then` callback, a microtask later). Once the module is here the
 *    factory hands React a thenable that resolves synchronously, which
 *    React reads as already-resolved and renders straight through.
 *
 * The fallback defaults to nothing, which is right for dialogs, sheets and
 * panels that appear on a click: they are prefetched at idle, so in practice
 * the chunk is there before the click; if it is not, the dialog opens a few
 * milliseconds later rather than drawing a spinner that would flash.
 *
 * A chunk that fails to load is thrown to the nearest error boundary (the
 * route's Reload screen, ADR 0067) — a stale tab after a deploy is fixed by a
 * reload, and a control that silently did nothing would be worse. A failed
 * `preload()` is forgotten, so the next attempt fetches again.
 */
export type Preloadable<P> = ComponentType<P> & { preload: () => Promise<unknown> };

// `any` is the only way to accept a component of arbitrary props here; the
// public type is recovered precisely through `ComponentProps<M[K]>`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

export function lazyComponent<M extends Record<K, AnyComponent>, K extends keyof M & string>(
  load: () => Promise<M>,
  name: K,
  fallback: ReactNode = null,
): Preloadable<ComponentProps<M[K]>> {
  let loaded: M | undefined;
  let pending: Promise<M> | undefined;

  const preload = (): Promise<M> => {
    if (loaded) return Promise.resolve(loaded);
    pending ??= load().then(
      (module) => {
        loaded = module;
        return module;
      },
      (error: unknown) => {
        pending = undefined;
        throw error;
      },
    );
    return pending;
  };

  const Lazy = lazy((): Promise<{ default: M[K] }> => {
    if (loaded) {
      const module = loaded;
      // A synchronous thenable: React's lazy initialiser calls `.then` and
      // checks the status right after, so this renders without suspending.
      return { then: (resolve: (value: { default: M[K] }) => void) => resolve({ default: module[name] }) } as Promise<{
        default: M[K];
      }>;
    }
    return preload().then((module) => ({ default: module[name] }));
  });

  function Loadable(props: ComponentProps<M[K]>) {
    return (
      <Suspense fallback={fallback}>
        <Lazy {...props} />
      </Suspense>
    );
  }
  Loadable.displayName = `Lazy(${name})`;
  return Object.assign(Loadable, { preload });
}
